import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useQuery } from '@apollo/client'
import {
  CreateCommitmentKpiLabelDocument,
  FetchGoalConfigsDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import {
  buildCommitmentKpiScheduleEntry,
  buildCommitmentKpiScheduleFormValues,
  CommitmentKpiScheduleForm,
  type CommitmentKpiScheduleFormValues,
  ErrorContext,
  getOrganizationIdFromPath,
} from '@salescore/client-common'
import { createStartAndEndAtOptions, dayOfWeekKeySchema } from '@salescore/features'
import { Button, Checkbox, Form, message, Row, Select } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useContext } from 'react'
import { useRecoilState } from 'recoil'
import z from 'zod'

import { kpiParameterAtom } from '../../../recoil/navigation/atoms'

export const kpiParameterSchema = z.object({
  goalConfig: z.object({
    id: z.string().min(1),
  }),
})

const commitmentKpiLabelPeriodSchema = z.object({
  startAt: z.string(),
  endAt: z.string(),
})

export const commitmentKpiScheduleFormValuesSchema: z.ZodType<CommitmentKpiScheduleFormValues> = z.object({
  timezone: z.string(),
  frequency: z.enum(['daily', 'weekly']),
  week: z.array(dayOfWeekKeySchema).default([]),
})

const commitmentKpiLabelSchema = z.object({
  viewId: z.string(),
  goalConfigId: z.string(),
  periods: z.array(commitmentKpiLabelPeriodSchema),
  schedule: commitmentKpiScheduleFormValuesSchema,
})
type CommitmentKpiLabelForm = z.infer<typeof commitmentKpiLabelSchema>

export const CommitmentKpiLabelForm = ({
  onFinish,
  kpiViewId,
  view,
}: {
  onFinish: () => void
  kpiViewId: string
  view: ViewFieldsFragment
}): ReactElement => {
  const [kpiParameter, setKpiParameter] = useRecoilState(kpiParameterAtom)
  const errorContext = useContext(ErrorContext)
  const [createCommitmentKpiLabelMutation, { loading }] = useMutation(CreateCommitmentKpiLabelDocument)

  const { data } = useQuery(FetchGoalConfigsDocument, {
    variables: { organizationId: getOrganizationIdFromPath(), viewId: view.id },
    fetchPolicy: 'cache-and-network',
  })
  const goalOptions =
    data?.goalConfigs
      .filter(
        (goalConfig) =>
          goalConfig.timeSpanType === 'month' && (goalConfig.userType === 'user' || goalConfig.userType === 'group'),
      )
      .map((goalConfig) => ({
        label: goalConfig.name,
        value: goalConfig.id,
      })) ?? []

  const kpiOptions = kpiParameter.goalConfig?.kpiViews
    .filter((view) => view.achievementStatusTargetValueType !== 'zero')
    .map((view) => ({ label: view.name, value: view.id }))

  const startAndEndAtOptions = createStartAndEndAtOptions()

  return (
    <PageHeader title={t(`魂KPIに設定`)}>
      <Form<CommitmentKpiLabelForm>
        layout="vertical"
        initialValues={{
          viewId: kpiViewId,
          goalConfigId: kpiParameter.goalConfig?.id,
          schedule: buildCommitmentKpiScheduleFormValues(undefined),
        }}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
        onFinish={async (values: CommitmentKpiLabelForm) => {
          const formValue = commitmentKpiLabelSchema.parse(values)
          const results = await createCommitmentKpiLabelMutation({
            variables: {
              organizationId: getOrganizationIdFromPath(),
              commitmentKpiLabel: {
                goalConfigId: formValue.goalConfigId,
                viewId: formValue.viewId,
                periods: formValue.periods,
                scheduleEntry: buildCommitmentKpiScheduleEntry(formValue.schedule),
              },
            },
            onCompleted: () => {
              void message.info(t(`魂KPIを設定しました`))
              onFinish()
            },
            onError: (error) => {
              errorContext.throwErrors(error)
            },
          })

          const now = new Date().toISOString()
          const createdCommitmentKpiLabels = results.data?.setCommitmentKpiLabel
          if (isNotNullish(createdCommitmentKpiLabels)) {
            for (const label of createdCommitmentKpiLabels) {
              if (label.startAt <= now && now < label.endAt) {
                setKpiParameter({
                  ...kpiParameter,
                  goalConfig: isNotNullish(kpiParameter.goalConfig)
                    ? {
                        ...kpiParameter.goalConfig,
                        commitmentKpiLabel: label,
                      }
                    : undefined,
                })
                break
              }
            }
          }
        }}
      >
        <Form.Item name={'viewId'} label={t(`KPI`)} layout="horizontal" className="mb-4" required>
          <Select placeholder={t(`選択してください`)} options={kpiOptions} disabled />
        </Form.Item>
        <Form.Item
          name={'goalConfigId'}
          label={t(`目標`)}
          layout="horizontal"
          rules={[{ required: true, message: t(`KPIを選択してください`) }]}
          className="mb-4"
        >
          <Select options={goalOptions} />
        </Form.Item>
        <Form.Item
          name={'periods'}
          label={t(`期間`)}
          rules={[{ required: true, message: t(`期間を選択してください`) }]}
          layout="horizontal"
          getValueFromEvent={(checkedValues: string[]) =>
            checkedValues.map((value) => commitmentKpiLabelPeriodSchema.parse(JSON.parse(value)))
          }
          getValueProps={(value: Array<{ startAt: string; endAt: string }>) => {
            if (!Array.isArray(value)) return { value: [] }
            return { value: value.map((v) => JSON.stringify(v)) }
          }}
        >
          <Checkbox.Group className="mt-1 flex flex-col ">
            {startAndEndAtOptions
              .map((option) => ({
                label: option.label,
                value: JSON.stringify(option.value),
              }))
              .map((option) => (
                <Checkbox key={option.label} value={option.value} className="mb-2">
                  {option.label}
                </Checkbox>
              ))}
          </Checkbox.Group>
        </Form.Item>
        <CommitmentKpiScheduleForm fieldNamePrefix={['schedule']} />
        <Form.Item>
          <Row justify="end">
            <Button type="primary" loading={loading} htmlType="submit">
              {t(`保存`)}
            </Button>
          </Row>
        </Form.Item>
      </Form>
    </PageHeader>
  )
}
